/**
 * Keşif store — deliberately separate from `useDebateStore`.
 *
 * `useDebateStore` carries one debate's volatile state through the `persist`
 * middleware into `macdm-session`. A crawl is neither volatile nor small: a
 * 5.000-page job would try to serialise megabytes into localStorage on every
 * frame, and every debate reset would wipe a running crawl's view. Same
 * reasoning as `personaLibraryStore.ts`, and like it this uses a plain
 * `create()` — there is no nested mutation here that needs immer.
 *
 * Ring buffers, not unbounded arrays: a live crawl emits one `crawl.page`
 * frame per page and several `crawl.log` lines per second. Keeping everything
 * would grow the store without bound over an hour-long job for scrollback no
 * one reads — the authoritative list is always one `listCrawlPages` call away.
 */
import { create } from "zustand";
import {
  EMPTY_COUNTERS,
  isTerminalStatus,
  type CrawlAsset,
  type CrawlEvent,
  type CrawlFrontierStats,
  type CrawlHost,
  type CrawlJob,
  type CrawlJobCounters,
  type CrawlJobPhase,
  type CrawlJobStatus,
  type CrawlLogLevel,
  type CrawlPage,
  type CrawlPlanSuggestion,
} from "../domain/kesif";
import {
  CrawlApiError,
  cancelCrawlJob,
  createCrawlJob,
  deleteCrawlJob,
  extendCrawlJob,
  getCrawlJob,
  listCrawlAssets,
  listCrawlJobs,
  listCrawlPages,
  openCrawlStream,
  pauseCrawlJob,
  resumeCrawlJob,
  startCrawlJob,
} from "../transport/crawlClient";

/** Live-feed caps. Chosen to stay under a few MB of retained strings. */
const MAX_LIVE_PAGES = 300;
const MAX_LIVE_ASSETS = 200;
const MAX_LOG_LINES = 250;

/**
 * Server page sizes. Exported because the pager divides `total` by exactly
 * these numbers — a component guessing its own page size would mis-render the
 * page count the moment either side changed.
 */
export const PAGES_PAGE_SIZE = 100;
export const ASSETS_PAGE_SIZE = 120;
export const JOBS_PAGE_SIZE = 40;

export interface CrawlLogLine {
  id: number;
  level: CrawlLogLevel;
  message: string;
  url: string;
  at: number;
}

export interface LiveMetrics {
  progress: number;
  activeHosts: number;
  inFlight: number;
  pagesPerMinute: number;
  etaSeconds: number | null;
}

const EMPTY_METRICS: LiveMetrics = {
  progress: 0,
  activeHosts: 0,
  inFlight: 0,
  pagesPerMinute: 0,
  etaSeconds: null,
};

const EMPTY_FRONTIER: CrawlFrontierStats = {
  pending: 0,
  leased: 0,
  done: 0,
  failed: 0,
  skipped: 0,
};

/**
 * Which slice of the server-side result set is on screen.
 *
 * This lives in the store rather than in the tab components for one reason:
 * the SSE handler has to know. Prepending a freshly crawled row is right on
 * the first page and wrong everywhere else — on page 3 it shoves the rows
 * being read out of view, and under a filter it injects a row that may not
 * even match. Only the store sees both the stream and the window.
 *
 * The filters are server-side too. Filtering the in-memory buffer instead
 * would silently mean "search the ~100 rows that happen to be loaded", which
 * reads as "no results" for a 5.000-page job.
 */
export interface PagesView {
  offset: number;
  q: string;
  host: string;
  pageType: string;
}

export interface AssetsView {
  offset: number;
  kind: string;
}

const EMPTY_PAGES_VIEW: PagesView = { offset: 0, q: "", host: "", pageType: "" };
const EMPTY_ASSETS_VIEW: AssetsView = { offset: 0, kind: "" };

function isFiltered(v: PagesView): boolean {
  return v.q !== "" || v.host !== "" || v.pageType !== "";
}

/**
 * The largest offset still inside a result set of `total` rows.
 *
 * Rows disappear underneath a pager: a job deleted from another tab, a
 * `Kanıt` prune, a filter that narrows the set below the current offset. With
 * no clamp the list renders empty next to a footer reading "sayfa 4 / 2",
 * which looks like a loading failure.
 */
function clampOffset(offset: number, total: number, size: number): number {
  if (offset <= 0 || total <= 0) return 0;
  return Math.min(offset, Math.max(0, Math.ceil(total / size) - 1) * size);
}

interface KesifState {
  // ---- job list ----
  jobs: CrawlJob[];
  total: number;
  jobsOffset: number;
  listLoading: boolean;
  /** Feature disabled server-side — the whole surface must hide, not error. */
  unavailable: boolean;
  listError: string | null;

  // ---- selected job ----
  job: CrawlJob | null;
  hosts: CrawlHost[];
  frontier: CrawlFrontierStats;
  counters: CrawlJobCounters;
  metrics: LiveMetrics;
  plan: CrawlPlanSuggestion | null;
  saturation: { novelRatio: number; reason: string } | null;
  detailLoading: boolean;
  detailError: string | null;

  // ---- live feeds ----
  pages: CrawlPage[];
  pagesTotal: number;
  pagesView: PagesView;
  pagesLoading: boolean;
  assets: CrawlAsset[];
  assetsTotal: number;
  assetsView: AssetsView;
  assetsLoading: boolean;
  logs: CrawlLogLine[];

  // ---- stream ----
  streaming: boolean;
  reconnecting: { attempt: number; max: number } | null;
  stopReason: string;

  loadJobs(opts?: { status?: string; offset?: number }): Promise<void>;
  openJob(jobId: string): Promise<void>;
  closeJob(): void;
  refreshPages(): Promise<void>;
  refreshAssets(): Promise<void>;
  /**
   * Patch the visible window and refetch. Changing a filter resets `offset` to
   * the first page — page 4 of the previous result set is usually past the end
   * of the new one. Pass `offset` explicitly to page without touching filters.
   */
  setPagesView(patch: Partial<PagesView>): Promise<void>;
  setAssetsView(patch: Partial<AssetsView>): Promise<void>;
  attach(jobId: string): void;
  detach(): void;
  start(jobId: string): Promise<void>;
  pause(jobId: string): Promise<void>;
  resume(jobId: string): Promise<void>;
  cancel(jobId: string): Promise<void>;
  /** Delete a job and its whole dataset. Resolves `false` on failure. */
  remove(jobId: string): Promise<boolean>;
  /**
   * Raise a stopped job's budget and continue it in place.
   *
   * Returns the number of frontier rows the continued run will consume, or
   * `null` on failure (the reason lands in `detailError`).
   */
  extend(jobId: string, patch: { maxPages: number; maxDepth?: number }): Promise<number | null>;
  /**
   * Copy a job's target and settings into a NEW job with a new budget.
   *
   * The escape hatch for a drained frontier, where continuing is impossible:
   * a fresh crawl of the same target. Returns the new job id, or `null`.
   */
  clone(jobId: string, patch: { maxPages: number; maxDepth?: number }): Promise<string | null>;
  applyEvent(evt: CrawlEvent): void;
  clearErrors(): void;
}

function describe(err: unknown): string {
  if (err instanceof CrawlApiError) {
    if (err.isCapacity && err.retryAfterS) {
      return `${err.message} (${err.retryAfterS} sn sonra tekrar deneyin)`;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Bilinmeyen hata";
}

/**
 * The stream lives outside React and outside the store's state so that a
 * re-render can never restart it and `persist` can never try to serialise an
 * `AbortController`. One controller per store instance; `attach` replaces it.
 */
let controller: AbortController | null = null;
let logSeq = 0;

/**
 * `"ssb"` -> `"ssb (2)"` -> `"ssb (3)"`.
 *
 * Copies need distinguishable names or the list becomes four rows called
 * `ssb` and the only way to tell them apart is the creation timestamp.
 */
export function nextCopyName(name: string): string {
  const m = /^(.*?)\s*\((\d+)\)$/.exec(name.trim());
  const base = (m?.[1] ?? name).trim().slice(0, 90);
  const n = m ? Number(m[2]) + 1 : 2;
  return `${base} (${n})`;
}

/** Newest-first, capped. `unshift`+`slice` keeps the render order trivial. */
function pushCapped<T>(list: T[], item: T, cap: number): T[] {
  const next = [item, ...list];
  return next.length > cap ? next.slice(0, cap) : next;
}

export const useKesifStore = create<KesifState>((set, get) => ({
  jobs: [],
  total: 0,
  jobsOffset: 0,
  listLoading: false,
  unavailable: false,
  listError: null,

  job: null,
  hosts: [],
  frontier: EMPTY_FRONTIER,
  counters: EMPTY_COUNTERS,
  metrics: EMPTY_METRICS,
  plan: null,
  saturation: null,
  detailLoading: false,
  detailError: null,

  pages: [],
  pagesTotal: 0,
  pagesView: EMPTY_PAGES_VIEW,
  pagesLoading: false,
  assets: [],
  assetsTotal: 0,
  assetsView: EMPTY_ASSETS_VIEW,
  assetsLoading: false,
  logs: [],

  streaming: false,
  reconnecting: null,
  stopReason: "",

  loadJobs: async (opts) => {
    if (get().listLoading) return;
    const offset = opts?.offset ?? get().jobsOffset;
    set({ listLoading: true, listError: null, jobsOffset: offset });
    try {
      const res = await listCrawlJobs({
        limit: JOBS_PAGE_SIZE,
        offset,
        ...(opts?.status ? { status: opts.status } : {}),
      });
      if (res.jobs.length === 0 && offset > 0) {
        // Deletions from another tab can leave the pager past the end.
        // Terminates: the clamp is strictly smaller, and offset 0 cannot
        // re-enter this branch.
        set({ listLoading: false });
        await get().loadJobs({
          ...opts,
          offset: clampOffset(offset, res.total, JOBS_PAGE_SIZE),
        });
        return;
      }
      set({ jobs: res.jobs, total: res.total, listLoading: false, unavailable: false });
    } catch (err) {
      const unavailable = err instanceof CrawlApiError && err.isUnavailable;
      set({
        listLoading: false,
        unavailable,
        // A disabled feature is not an error to report; the UI hides instead.
        listError: unavailable ? null : describe(err),
      });
    }
  },

  openJob: async (jobId) => {
    set({
      detailLoading: true,
      detailError: null,
      plan: null,
      saturation: null,
      stopReason: "",
      logs: [],
      // A different job starts on page 1 with no filters; carrying the previous
      // job's offset over would open on an empty list.
      pagesView: EMPTY_PAGES_VIEW,
      assetsView: EMPTY_ASSETS_VIEW,
    });
    try {
      const detail = await getCrawlJob(jobId);
      set({
        job: detail.job,
        hosts: detail.hosts,
        frontier: detail.frontier,
        counters: detail.job.counters,
        detailLoading: false,
      });
      await Promise.allSettled([get().refreshPages(), get().refreshAssets()]);
      // Attaching only when the backend says an engine is live avoids a
      // guaranteed 409 for every historical job the user opens.
      if (detail.live && !isTerminalStatus(detail.job.status)) {
        get().attach(jobId);
      }
    } catch (err) {
      set({ detailLoading: false, detailError: describe(err) });
    }
  },

  closeJob: () => {
    get().detach();
    set({
      job: null,
      hosts: [],
      frontier: EMPTY_FRONTIER,
      counters: EMPTY_COUNTERS,
      metrics: EMPTY_METRICS,
      plan: null,
      saturation: null,
      pages: [],
      pagesTotal: 0,
      pagesView: EMPTY_PAGES_VIEW,
      assets: [],
      assetsTotal: 0,
      assetsView: EMPTY_ASSETS_VIEW,
      logs: [],
      stopReason: "",
      detailError: null,
    });
  },

  refreshPages: async () => {
    const job = get().job;
    if (!job) return;
    const v = get().pagesView;
    set({ pagesLoading: true });
    try {
      const res = await listCrawlPages(job.id, {
        limit: PAGES_PAGE_SIZE,
        offset: v.offset,
        ...(v.host ? { host: v.host } : {}),
        ...(v.pageType ? { pageType: v.pageType } : {}),
        ...(v.q ? { q: v.q } : {}),
      });
      const clamped = clampOffset(v.offset, res.total, PAGES_PAGE_SIZE);
      if (res.pages.length === 0 && clamped !== v.offset) {
        set({ pagesView: { ...v, offset: clamped }, pagesLoading: false });
        await get().refreshPages();
        return;
      }
      set({ pages: res.pages, pagesTotal: res.total, pagesLoading: false });
    } catch (err) {
      set({ pagesLoading: false, detailError: describe(err) });
    }
  },

  refreshAssets: async () => {
    const job = get().job;
    if (!job) return;
    const v = get().assetsView;
    set({ assetsLoading: true });
    try {
      const res = await listCrawlAssets(job.id, {
        limit: ASSETS_PAGE_SIZE,
        offset: v.offset,
        ...(v.kind ? { kind: v.kind } : {}),
      });
      const clamped = clampOffset(v.offset, res.total, ASSETS_PAGE_SIZE);
      if (res.assets.length === 0 && clamped !== v.offset) {
        set({ assetsView: { ...v, offset: clamped }, assetsLoading: false });
        await get().refreshAssets();
        return;
      }
      set({ assets: res.assets, assetsTotal: res.total, assetsLoading: false });
    } catch (err) {
      set({ assetsLoading: false, detailError: describe(err) });
    }
  },

  setPagesView: async (patch) => {
    const cur = get().pagesView;
    set({
      pagesView: { ...cur, ...patch, ...(patch.offset === undefined ? { offset: 0 } : {}) },
    });
    await get().refreshPages();
  },

  setAssetsView: async (patch) => {
    const cur = get().assetsView;
    set({
      assetsView: { ...cur, ...patch, ...(patch.offset === undefined ? { offset: 0 } : {}) },
    });
    await get().refreshAssets();
  },

  attach: (jobId) => {
    get().detach();
    const ac = new AbortController();
    controller = ac;
    set({ streaming: true, reconnecting: null });

    void openCrawlStream({
      jobId,
      signal: ac.signal,
      onEvent: (evt) => get().applyEvent(evt),
      onReconnecting: (attempt, max) => set({ reconnecting: { attempt, max } }),
      onNotLive: () => {
        // The engine finished between hydrate and attach. Re-read the terminal
        // state rather than leaving the UI on stale "running".
        set({ streaming: false });
        void get().openJob(jobId);
      },
      onError: (err) => set({ streaming: false, detailError: describe(err) }),
    })
      .catch(() => {
        /* already surfaced through onError */
      })
      .finally(() => {
        if (controller === ac) {
          controller = null;
          set({ streaming: false, reconnecting: null });
        }
      });
  },

  detach: () => {
    if (controller) {
      controller.abort();
      controller = null;
    }
    set({ streaming: false, reconnecting: null });
  },

  start: async (jobId) => {
    try {
      await startCrawlJob(jobId);
      get().attach(jobId);
      await get().openJob(jobId);
    } catch (err) {
      set({ detailError: describe(err) });
    }
  },

  pause: async (jobId) => {
    try {
      await pauseCrawlJob(jobId);
    } catch (err) {
      set({ detailError: describe(err) });
    }
  },

  resume: async (jobId) => {
    try {
      const res = await resumeCrawlJob(jobId);
      // A resume that had to RELAUNCH the engine returns a stream path; one
      // that merely released a parked engine does not, and the existing
      // stream is still attached.
      if (res.streamPath && !get().streaming) get().attach(jobId);
    } catch (err) {
      set({ detailError: describe(err) });
    }
  },

  cancel: async (jobId) => {
    try {
      await cancelCrawlJob(jobId);
    } catch (err) {
      set({ detailError: describe(err) });
    }
  },

  remove: async (jobId) => {
    try {
      // Detach first: the stream would keep pushing frames for rows that no
      // longer exist, and the reconnect loop would retry a 404 forever.
      if (get().job?.id === jobId) get().detach();
      await deleteCrawlJob(jobId);
      // The open job is deliberately NOT cleared here. Clearing it makes the
      // detail panel fall into its "Tarama bulunamadı" branch and hold there
      // for as long as the list refresh takes — an error message shown to the
      // person who just deliberately deleted the thing. `remove` only reports;
      // the caller navigates, and until it does the stale row is harmless
      // because the stream is already detached.
      set({ jobs: get().jobs.filter((j) => j.id !== jobId) });
      void get().loadJobs();
      return true;
    } catch (err) {
      set({ listError: describe(err), detailError: describe(err) });
      return false;
    }
  },

  extend: async (jobId, patch) => {
    try {
      const res = await extendCrawlJob(jobId, patch);
      // Re-read before attaching: the job row is now `running` with a raised
      // budget, and the progress bar divides by `policy.maxPages`.
      await get().openJob(jobId);
      if (!get().streaming) get().attach(jobId);
      return res.resumable;
    } catch (err) {
      set({ detailError: describe(err) });
      return null;
    }
  },

  clone: async (jobId, patch) => {
    const s = get();
    // Either surface can trigger this, and the list wire carries the full job
    // shape, so no extra round trip is needed to read the source settings.
    const src = s.job?.id === jobId ? s.job : s.jobs.find((j) => j.id === jobId);
    if (!src) {
      set({ listError: "Kaynak tarama bulunamadı; listeyi yenileyin." });
      return null;
    }
    try {
      const res = await createCrawlJob({
        name: nextCopyName(src.name || src.seedUrls[0] || "Keşif"),
        seedUrls: src.seedUrls,
        // Everything except the budget is carried over verbatim: a copy that
        // silently changed scope or politeness would not be comparable with
        // the crawl it was copied from.
        policy: { ...src.policy, ...patch },
        autoStart: true,
      });
      await get().loadJobs({ offset: 0 });
      return res.job.id;
    } catch (err) {
      set({ listError: describe(err), detailError: describe(err) });
      return null;
    }
  },

  applyEvent: (evt) => {
    switch (evt.type) {
      case "crawl.job": {
        const job = get().job;
        set({
          counters: evt.counters,
          ...(job && job.id === evt.jobId
            ? {
                job: {
                  ...job,
                  status: evt.status as CrawlJobStatus,
                  phase: evt.phase as CrawlJobPhase,
                  counters: evt.counters,
                  error: evt.error,
                  degradedReason: evt.degradedReason,
                },
              }
            : {}),
        });
        break;
      }
      case "crawl.progress": {
        const job = get().job;
        set({
          counters: evt.counters,
          metrics: {
            progress: evt.progress,
            activeHosts: evt.activeHosts,
            inFlight: evt.inFlight,
            pagesPerMinute: evt.pagesPerMinute,
            etaSeconds: evt.etaSeconds,
          },
          ...(job && job.id === evt.jobId
            ? { job: { ...job, phase: evt.phase, counters: evt.counters } }
            : {}),
        });
        break;
      }
      case "crawl.page": {
        const s = get();
        // Under a filter the row cannot be placed: the frame carries no verdict
        // on whether it matches, so neither the list nor the total may move.
        // «Yenile» re-reads both from the server.
        if (isFiltered(s.pagesView)) break;
        // Past the first page, the total is still live but the rows are frozen —
        // prepending would push whatever is being read out of view.
        if (s.pagesView.offset > 0) {
          set({ pagesTotal: s.pagesTotal + 1 });
          break;
        }
        // De-duplicate against the hydrated list: a page fetched between the
        // REST read and the stream attach arrives through both paths.
        const without = s.pages.filter((p) => p.id !== evt.page.id);
        const next = [evt.page, ...without];
        set({
          pages: next.length > MAX_LIVE_PAGES ? next.slice(0, MAX_LIVE_PAGES) : next,
          pagesTotal: without.length === s.pages.length ? s.pagesTotal + 1 : s.pagesTotal,
        });
        break;
      }
      case "crawl.asset": {
        const s = get();
        if (s.assetsView.kind !== "") break;
        if (s.assetsView.offset > 0) {
          set({ assetsTotal: s.assetsTotal + evt.assets.length });
          break;
        }
        const known = new Set(s.assets.map((a) => a.id));
        const fresh = evt.assets.filter((a) => !known.has(a.id));
        if (fresh.length === 0) break;
        const next = [...fresh, ...s.assets];
        set({
          assets: next.length > MAX_LIVE_ASSETS ? next.slice(0, MAX_LIVE_ASSETS) : next,
          assetsTotal: s.assetsTotal + fresh.length,
        });
        break;
      }
      case "crawl.host": {
        const s = get();
        const idx = s.hosts.findIndex((h) => h.host === evt.host.host);
        const hosts = idx === -1 ? [...s.hosts, evt.host] : [...s.hosts];
        if (idx !== -1) hosts[idx] = evt.host;
        set({ hosts });
        break;
      }
      case "crawl.plan":
        set({ plan: evt.plan });
        break;
      case "crawl.log":
        set({
          logs: pushCapped(
            get().logs,
            {
              id: (logSeq += 1),
              level: evt.level,
              message: evt.message,
              url: evt.url,
              at: evt.at,
            },
            MAX_LOG_LINES,
          ),
        });
        break;
      case "crawl.saturated":
        set({ saturation: { novelRatio: evt.novelRatio, reason: evt.reason } });
        break;
      case "crawl.done": {
        const job = get().job;
        set({
          counters: evt.counters,
          stopReason: evt.stopReason,
          streaming: false,
          ...(job && job.id === evt.jobId
            ? {
                job: {
                  ...job,
                  status: evt.status as CrawlJobStatus,
                  phase: "done" as CrawlJobPhase,
                  counters: evt.counters,
                },
              }
            : {}),
        });
        break;
      }
      case "crawl.error":
        set({ detailError: `${evt.message} (${evt.code})` });
        break;
      default: {
        // Exhaustiveness guard: adding a variant to `CrawlEvent` without
        // handling it here becomes a compile error rather than silence.
        const never: never = evt;
        void never;
      }
    }
  },

  clearErrors: () => set({ listError: null, detailError: null }),
}));
